import { forwardRef, RefObject, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link as RouterLink } from 'react-router'
import {
  CallOutlined,
  ContentCopy,
  DragIndicator,
  EmailOutlined,
  FolderOpenOutlined,
  NotificationsOffOutlined,
  PhoneIphoneOutlined,
  TextsmsOutlined,
} from '@mui/icons-material'
import { Alert, Box, Checkbox, CheckboxProps, Chip, Grid, IconButton, Link, Tooltip, Typography, useTheme } from '@mui/material'
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_REORDER_COL_DEF,
  GridCellParams,
  GridColDef,
  GridColumnHeaders,
  GridRowOrderChangeParams,
  GridRowSelectionModel,
  GridRowsProp,
} from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'

import ImpersonateInMobileDialog from '../../../../components/impersonate-in-mobile-dialog'
import UserModal from '../../../../components/user-modal'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { prettifyPhoneNumber } from '../../../../utils/helpers'
import useClipboard from '../../../../utils/hooks/useClipboard'
import useStickyHeaders from '../../../../utils/hooks/useStickyHeaders'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'
import useAccountStyles from '../../index.styles'

import { sortFolderUsers, updateRowPosition } from './helpers'
import useStyles from './index.styles'
import MoreOptionsMenu from './more-options-menu'
import PeopleTableToolbar from './people-table-toolbar'

const TabPanelPeople = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const accountClasses = useAccountStyles
  const theme = useTheme()
  const containerRef = useRef(null)
  const host = useHost()
  const auth = useAuth()
  const { pageRefreshCounter } = useContext(AppContext)

  const { folder, folderAttributes, setFolderAttributes, searchText, peopleTabRefreshCounter, setPeopleTabRefreshCounter } = useContext(AccountsContext)
  const { tenantId } = useContext(AppContext)
  const { enqueueSnackbar } = useSnackbar()
  const { clickToCopy } = useClipboard()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [peopleTableData, setPeopleTableData] = useState<IUserType[]>([])
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [menuUser, setMenuUser] = useState<IUserType | undefined>()
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })

  // has to be outside MoreOptionsMenu so that modals can be visible once the menu closes
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false)
  const [showImpersonateDialog, setShowImpersonateDialog] = useState<boolean>(false)

  const isParticipant = folder?.type === 'Participant'
  const showHeaderColumns = selectedIds.ids.size === 0

  const { doStickyHeaders } = useStickyHeaders({ containerRef })

  const getFolderUsers = async () => {
    if (!host || !auth.user?.access_token) return
    if (folder?.id) {
      setIsLoading(true)
      const res = await Api.post(`${host}/api/users/query?detail=true`, {
        filters: [{ fieldName: 'folders', operator: 'contains', value: folder.id }],
        first: 10000,
      })
      if (res.ok) {
        setError('')
        setPeopleTableData(res.body.results)
      } else {
        setError('Something went wrong, and list of people did not load')
      }
      setIsLoading(false)
    }
  }

  const fetchData = useEffectEvent(getFolderUsers)
  useEffect(() => {
    fetchData()
  }, [folder?.id, peopleTabRefreshCounter, host, pageRefreshCounter])

  const columns: GridColDef[] = useMemo(
    () => [
      { ...GRID_CHECKBOX_SELECTION_COL_DEF, renderHeader: () => null },
      ...(isParticipant
        ? [
            {
              ...GRID_REORDER_COL_DEF,
            },
            {
              field: 'index',
              renderHeader: () => null,
              sortable: false,
              maxWidth: 50,
              flex: 1,
              resizable: false,
              renderCell: (cellParams: GridCellParams<IUserType, number>) =>
                (cellParams.row.preferences?.notification?.length ?? 0) > 0 && folderAttributes?.notifyPriority.includes(cellParams.row.id) ? (
                  cellParams.value
                ) : (
                  <Tooltip title={(cellParams.row.preferences?.notification?.length ?? 0) === 0 ? 'Do not contact' : 'No notification priority set'}>
                    <NotificationsOffOutlined fontSize="small" color="secondary" />
                  </Tooltip>
                ),
            },
          ]
        : []),
      {
        field: 'name',
        headerName: 'Name',
        editable: false,
        sortable: !isParticipant,
        flex: 1,
        renderCell: (cellParams: GridCellParams<IUserType, string>) => {
          const name = cellParams?.row?.information ? `${cellParams.row.information.first} ${cellParams.row.information.last}` : ''
          return (
            <Tooltip title={name}>
              <Link
                to={`/tenant/${tenantId}/people/${cellParams.row.id}`}
                color="primary"
                underline="hover"
                component={RouterLink}
                className="fs-exclude"
                sx={sharedClasses.overflowText}
              >
                {name}
              </Link>
            </Tooltip>
          )
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        editable: false,
        sortable: !isParticipant,
        flex: 1.5,
        renderCell: (cellParams: GridCellParams<IUserType, string>) => (
          <>
            <Tooltip title={cellParams.row.email ?? ''}>
              <Link
                href={`mailto:${cellParams.row.email}`}
                color="primary"
                underline="hover"
                rel="noopener noreferrer"
                target="_top"
                className="fs-exclude"
                sx={sharedClasses.overflowText}
              >
                {cellParams.row.email ?? ''}
              </Link>
            </Tooltip>

            {cellParams.row.email && (
              <Tooltip title="Copy email to clipboard" arrow={true} placement="top">
                <IconButton
                  onClick={() => {
                    clickToCopy(cellParams.row.email)
                  }}
                  sx={classes.copyIconButton}
                  aria-label="Copy email to clipboard"
                >
                  <ContentCopy color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        field: 'role',
        headerName: 'Role',
        editable: false,
        sortable: !isParticipant,
        flex: 1,
        renderCell: (cellParams: GridCellParams<IUserType, string>) => {
          const roleName = cellParams?.row?.roleName ? cellParams.row.roleName.charAt(0).toUpperCase() + cellParams.row.roleName.slice(1) : ''
          return (
            <Tooltip title={roleName}>
              <Typography variant="body2" sx={sharedClasses.overflowText}>
                {roleName}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'phone',
        headerName: 'Primary/Mobile',
        editable: false,
        sortable: !isParticipant,
        flex: 1,
        renderCell: (cellParams: GridCellParams<IUserType, string>) => {
          const phone = prettifyPhoneNumber(cellParams?.row?.phone ?? '')
          return (
            <Tooltip title={phone}>
              <Typography variant="body2" className="fs-exclude" sx={sharedClasses.overflowText}>
                {phone}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'statusMembership',
        headerName: 'Membership Status',
        editable: false,
        sortable: true,
        maxWidth: 200,
        flex: 1.5,
        renderCell: (cellParams: GridCellParams<IUserType, string>) => (
          <Chip
            sx={{
              ...(cellParams.value === 'active' ? classes.activeMembershipChip(theme) : {}),
              ...(cellParams.value === 'invited' ? classes.invitedMembershipChip(theme) : {}),
            }}
            label={
              <Grid container={true} sx={sharedClasses.centerContent}>
                <Grid sx={classes.membershipChipLabel}>{cellParams.value}</Grid>
              </Grid>
            }
          />
        ),
      },
      ...(isParticipant
        ? [
            {
              field: 'preferences',
              headerName: 'Preferences',
              editable: false,
              sortable: !isParticipant,
              flex: 1,
              renderCell: (cellParams: GridCellParams<IUserType, string[]>) => {
                const notifications = cellParams?.row?.preferences?.notification ?? []
                return (
                  <Grid container={true} sx={[sharedClasses.centerContentVertical, { gap: 1 }, classes.preferencesContainer]}>
                    {(notifications.length ?? 0) === 0 && (
                      <Tooltip title="Do not contact">
                        <Typography variant="body2" sx={sharedClasses.overflowText}>
                          Do not contact
                        </Typography>
                      </Tooltip>
                    )}
                    {notifications.length > 0 && (
                      <>
                        {notifications.includes('sms') && (
                          <Grid>
                            <Tooltip title="Text">
                              <TextsmsOutlined fontSize="small" color="secondary" sx={classes.preferenceIcon} />
                            </Tooltip>
                          </Grid>
                        )}
                        {notifications.includes('push') && (
                          <Grid>
                            <Tooltip title="Push">
                              <PhoneIphoneOutlined fontSize="small" color="secondary" sx={classes.preferenceIcon} />
                            </Tooltip>
                          </Grid>
                        )}
                        {notifications.includes('phone') && (
                          <Grid>
                            <Tooltip title="Phone">
                              <CallOutlined fontSize="small" color="secondary" sx={classes.preferenceIcon} />
                            </Tooltip>
                          </Grid>
                        )}
                        {notifications.includes('email') && (
                          <Grid>
                            <Tooltip title="Email">
                              <EmailOutlined fontSize="small" color="secondary" sx={classes.preferenceIcon} />
                            </Tooltip>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                )
              },
            },
          ]
        : []),
      {
        field: 'accounts',
        headerName: 'Folders',
        editable: false,
        sortable: !isParticipant,
        maxWidth: 100,
        flex: 1,
        renderCell: (cellParams: GridCellParams<IUserType, number>) => {
          const folderCount = cellParams?.row?.folders.length
          const userId = cellParams?.row?.id

          return (
            <Link
              component={RouterLink}
              to={{
                pathname: `/tenant/${tenantId}/people/${userId}`,
                hash: '#user-accounts',
              }}
              underline="hover"
              aria-label="View User Accounts"
            >
              <Chip
                sx={classes.accountsChip}
                label={
                  <Grid container={true} sx={sharedClasses.centerContent}>
                    <Grid sx={[sharedClasses.centerContentVertical, classes.chipIcon]}>
                      <FolderOpenOutlined fontSize="small" />
                    </Grid>
                    <Grid>{folderCount}</Grid>
                  </Grid>
                }
              />
            </Link>
          )
        },
      },
      {
        field: 'options-menu',
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        maxWidth: 42,
        flex: 1,
        renderHeader: () => null,
        renderCell: (cellParams: GridCellParams<IUserType>) => (
          <>
            <MoreOptionsMenu
              setShowEditUserModal={(value: boolean) => {
                setMenuUser(cellParams.row)
                setShowEditUserModal(value)
              }}
              user={cellParams.row}
              setShowImpersonateDialog={(value: boolean) => {
                setMenuUser(cellParams.row)
                setShowImpersonateDialog(value)
              }}
            />
          </>
        ),
      },
    ],
    [
      isParticipant,
      folderAttributes?.notifyPriority,
      tenantId,
      sharedClasses.overflowText,
      sharedClasses.centerContent,
      sharedClasses.centerContentVertical,
      classes,
      clickToCopy,
      theme,
    ]
  )

  const rows: GridRowsProp = useMemo(() => {
    let results = peopleTableData
    if (searchText) {
      results = results.filter((item) => {
        const name = `${item.information?.first} ${item.information?.last}`
        return name.toLowerCase().includes(searchText.toLowerCase()) || item.email.toLowerCase().includes(searchText.toLowerCase())
      })
    }
    return sortFolderUsers(results, folderAttributes?.notifyPriority).map((user, index) => ({
      ...user,
      index: index + 1,
      __reorder__: `${user.information?.first} ${user.information?.last}`,
    }))
  }, [folderAttributes?.notifyPriority, peopleTableData, searchText])

  return (
    <>
      {error && (
        <Grid container={true} sx={[sharedClasses.centerContentHorizontal, classes.errorContainer]}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {!error && folder && (
        <>
          <Box sx={accountClasses.dataGridContainer} data-cy="notificationContacts">
            <DataGridPro
              ref={containerRef}
              columns={columns}
              rows={rows}
              loading={isLoading}
              slots={{
                toolbar: showHeaderColumns
                  ? () => null
                  : () => (
                      <PeopleTableToolbar
                        users={rows as IUserType[]}
                        selectedIds={Array.from(selectedIds.ids.keys())}
                        setIsDeleting={setIsDeleting}
                        getFolderUsers={getFolderUsers}
                      />
                    ),
                columnHeaders: showHeaderColumns ? GridColumnHeaders : forwardRef(() => null),
                rowReorderIcon: () => <DragIndicator color="action" />,
                // @ts-expect-error type mismatch
                baseCheckbox: forwardRef(
                  (props: CheckboxProps, ref: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement | null> | null | undefined) => (
                    <Checkbox
                      {...props}
                      slotProps={{ ...props.slotProps, input: { 'aria-label': 'Select row' } }}
                      ref={ref}
                      color="primary"
                      disabled={isDeleting || (selectedIds.ids.size >= 5 && !props.checked)}
                    />
                  )
                ),
              }}
              rowHeight={56}
              autosizeOptions={{ expand: true }}
              checkboxSelection
              disableRowSelectionOnClick
              hideFooter
              rowReordering={isParticipant}
              onRowOrderChange={async (params: GridRowOrderChangeParams) => {
                if (!auth.user?.access_token) return
                await updateRowPosition(
                  params.oldIndex,
                  params.targetIndex,
                  peopleTableData,
                  folder,
                  enqueueSnackbar,
                  setPeopleTableData,
                  setIsLoading,
                  setFolderAttributes,
                  host
                )
              }}
              onRowSelectionModelChange={(rowSelectionModel) => {
                setSelectedIds(rowSelectionModel)
                setTimeout(() => doStickyHeaders(), 100)
              }}
              showToolbar
            />

            {menuUser && (
              <>
                <UserModal
                  mode="edit"
                  open={showEditUserModal}
                  setOpen={setShowEditUserModal}
                  user={menuUser}
                  setPeopleTabRefreshCounter={setPeopleTabRefreshCounter}
                />

                <ImpersonateInMobileDialog user={menuUser} setShowImpersonateDialog={setShowImpersonateDialog} showImpersonateDialog={showImpersonateDialog} />
              </>
            )}
          </Box>
        </>
      )}
    </>
  )
}

export default TabPanelPeople
